/* Domovoi · native sign-in with Apple and Google, inside the app (D2.3).

   A web OAuth redirect can't come back into the app, so here the phone's own
   sheet signs you in (@capgo/capacitor-social-login) and hands us an ID token,
   which Supabase checks and turns into a session (signInWithIdToken). On the
   web this module is never used: the pages keep their redirect flow.

   The nonce: a random value whose SHA-256 goes to Apple or Google, which put
   it into the ID token; we give Supabase the raw value and it checks the two
   match, so a token lifted from elsewhere can't be replayed here.

   With core/platform.js, core/native.js and core/push.js, the only modules
   that import Capacitor. */
import { supabase } from './supabase.js';
import { isNative, platform } from './platform.js';
import { GOOGLE_WEB_CLIENT_ID, GOOGLE_IOS_CLIENT_ID } from './config.js';

const NAME_KEY = 'domovoi.provider_first_name';
let ready = null;
async function plugin() {
  const { SocialLogin } = await import('@capgo/capacitor-social-login');
  if (!ready) {
    // Apple only on iPhones: on Android the plugin's Apple sign-in is a web round trip that needs a
    // redirectUrl, and initialising it without one throws for every provider ("apple.android.redirectUrl
    // is null or empty"), Google included.
    const google = { iOSClientId: GOOGLE_IOS_CLIENT_ID, iOSServerClientId: GOOGLE_WEB_CLIENT_ID, webClientId: GOOGLE_WEB_CLIENT_ID, mode: 'online' };
    ready = SocialLogin.initialize(platform() === 'ios' ? { apple: { clientId: 'app.domovoi.mobile' }, google } : { google })
      .catch((e) => { ready = null; throw e; });
  }
  await ready;
  return { SocialLogin };   // wrapped: a plugin proxy must never be what a promise resolves to (see core/push.js)
}

async function sha256hex(s) {
  const d = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return Array.from(new Uint8Array(d)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/* Which buttons to show: Apple only on iPhones (Apple's rule is about iOS; on
   Android it would need a web round trip), Google on both. */
export function nativeProviders() {
  if (!isNative()) return [];
  return platform() === 'ios' ? ['apple', 'google'] : ['google'];
}

/* Resolves to the Supabase { user, session }, to null if you closed the sheet,
   and throws with a readable message otherwise. */
export async function nativeSignIn(provider) {
  const { SocialLogin } = await plugin();
  const raw = crypto.randomUUID ? crypto.randomUUID() + crypto.randomUUID() : String(Math.random()).slice(2) + Date.now();
  const hashed = await sha256hex(raw);
  let res;
  try {
    // Google's sign-in always asks for email, profile and openid. Naming them again is fine on an
    // iPhone, but on Android the plugin refuses any scopes unless MainActivity implements its
    // ModifiedMainActivityForSocialLoginPlugin (1.1.1 and later), so Android asks for none.
    const google = platform() === 'android' ? { nonce: hashed } : { scopes: ['email', 'profile'], nonce: hashed };
    res = await SocialLogin.login(provider === 'apple'
      ? { provider: 'apple', options: { scopes: ['email', 'name'], nonce: hashed } }
      : { provider: 'google', options: google });
  } catch (e) {
    const m = String((e && (e.message || e.code)) || e);
    if (/cancel|1001|12501|-5|closed/i.test(m)) return null;   // you backed out of the sheet
    if (window.DVTrackError) window.DVTrackError(e instanceof Error ? e : new Error(m), { where: 'native_sign_in', provider });
    throw new Error('That didn’t go through. Try again, or use your email.');
  }
  const r = (res && res.result) || {};
  const token = r.idToken;
  if (!token) throw new Error('That didn’t go through. Try again, or use your email.');
  // Apple tells us your first name once, the very first time; Google every time.
  // Apple's rules say we mustn't then ask you for it, so the join flow's name
  // step uses it (providerFirstName). Stashed before signing in: the sign-in
  // event reaches the join page before updateUser below could.
  const p = r.profile || {};
  const given = String(p.givenName || p.given_name || String(p.name || '').split(' ')[0] || '').trim();
  if (given) { try { sessionStorage.setItem(NAME_KEY, given.slice(0, 40)); } catch (e) {} }
  const { data, error } = await supabase().auth.signInWithIdToken({ provider, token, nonce: raw });
  if (error) throw new Error(error.message || 'Sign-in didn’t work. Try again.');
  if (given && data.user && !(data.user.user_metadata || {}).first_name) {
    try { await supabase().auth.updateUser({ data: { first_name: given } }); } catch (e) {}
  }
  if (window.DVTrack) window.DVTrack('native_sign_in', { product: 'account', provider });
  return data;
}

/* Google Calendar for the week, read-only (src/week/gcal.js). The phone's Google sign-in keeps
   the access, so later reads need no prompt: `interactive: false` refreshes the token quietly (on Android
   through the plugin's refresh(), which getAuthorizationCode doesn't do there).
   Asking for it always shows Google's sheet — a nonce makes the plugin sign in afresh instead of
   restoring an earlier sign-in that never had the calendar permissions. */
export async function nativeGoogleToken(scopes, opts) {
  const { SocialLogin } = await plugin();
  if (!(opts && opts.interactive)) {
    let r;
    try {
      // An iPhone refreshes inside getAuthorizationCode. Android's only checks the tokens it kept (an hour each) and,
      // once they've run out, signs you out of the plugin, so every launch after an hour asked you to reconnect
      // (Ben, 1 Nov). refresh() first gets new ones quietly from the phone's Google account, with the calendar
      // permissions it kept; it does nothing while they're still good.
      if (platform() === 'android') await SocialLogin.refresh({ provider: 'google', options: {} });
      r = await SocialLogin.getAuthorizationCode({ provider: 'google' });
    } catch (e) {
      // Offline, try again next time. Otherwise Google wouldn't refresh it (you took the access
      // away, or signed out of Google on the phone), and that comes back as a plugin error rather
      // than a 401: treat it as 'auth' so the week asks you to reconnect.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) throw e;
      throw Object.assign(new Error('Google wouldn’t refresh the access'), { code: 'auth', cause: e });
    }
    if (!r || !r.accessToken) throw Object.assign(new Error('no token'), { code: 'auth' });
    return { token: r.accessToken, email: null };
  }
  const raw = crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2) + Date.now();
  let res;
  try {
    res = await SocialLogin.login({ provider: 'google', options: { scopes: ['email', 'profile'].concat(scopes), nonce: await sha256hex(raw) } });
  } catch (e) {
    const m = String((e && (e.message || e.code)) || e);
    if (/cancel|1001|12501|-5|closed/i.test(m)) throw Object.assign(new Error('cancelled'), { code: 'cancelled' });
    throw e;
  }
  const r = (res && res.result) || {};
  const at = r.accessToken && (r.accessToken.token || r.accessToken);
  if (!at || typeof at !== 'string') throw Object.assign(new Error('no token'), { code: 'auth' });
  return { token: at, email: (r.profile && r.profile.email) || null };
}

/* The first name Apple or Google gave us for this person, if any: from this
   sign-in (sessionStorage) or from the account's metadata. */
export function providerFirstName(user) {
  const m = (user && user.user_metadata) || {};
  let n = '';
  try { n = sessionStorage.getItem(NAME_KEY) || ''; } catch (e) {}
  n = n || m.first_name || m.given_name || String(m.full_name || m.name || '').split(' ')[0] || '';
  n = String(n).trim();
  return /@/.test(n) ? '' : n.slice(0, 40);
}

/* Sign-out: forget the provider's own session too, so the next person on this
   phone gets the account picker. After deleting your account, `forget` also
   drops the name Apple gave us, which the plugin keeps for later sign-ins. */
export async function nativeSignOut(opts) {
  if (!isNative()) return;
  const forget = !!(opts && opts.forget);
  try {
    const { SocialLogin } = await plugin();
    await Promise.allSettled([
      SocialLogin.logout({ provider: 'google' }),
      platform() === 'ios' ? SocialLogin.logout({ provider: 'apple', clearCachedUserInfo: forget }) : null,
    ]);
  } catch (e) {}
}
